import { createContext, useContext, useMemo, type ReactNode } from "react";
import { ApiClient, type TokenReader } from "../api/client";

/**
 * El cliente HTTP vive en un contexto para que los hooks de consulta no
 * tengan que reconstruirlo en cada render (rompería la caché de TanStack
 * Query al cambiar la identidad de las funciones).
 */
const ApiContext = createContext<ApiClient | null>(null);

export interface ApiProviderProps {
  children: ReactNode;
  getToken: TokenReader;
  onUnauthorized: () => void;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

export function ApiProvider({ children, getToken, onUnauthorized, baseUrl, fetchImpl }: ApiProviderProps) {
  const client = useMemo(
    () =>
      new ApiClient({
        baseUrl: baseUrl ?? (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "",
        getToken,
        onUnauthorized,
        ...(fetchImpl ? { fetchImpl } : {}),
      }),
    [getToken, onUnauthorized, baseUrl, fetchImpl],
  );

  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (!client) throw new Error("useApi se ha usado fuera de <ApiProvider>");
  return client;
}
